import React, { ChangeEvent, createRef, Component } from "react";
import { inject, observer } from "mobx-react";

import { Utils } from "@/common/utils";

import { Link } from "react-router-dom";
import {
    Alert,
    Button,
    Col,
    Form,
    Modal,
    Row,
    Spinner,
    Tab,
    Nav,
} from "react-bootstrap";
import { Committee, Course, Student } from "Stores/commonStore";
import { MilestoneStatus } from "Stores/milestoneStore";
import DatePicker from "react-datepicker";

import { TicketCommentList } from "./CommentList";

import {
    AppStore,
    AttachmentStore,
    CommentStore,
    CommonStore,
    HistoryStore,
    MilestoneStore,
    NotificationViewerStore,
    SnapshotStore,
    StrategicPlanStore,
    TicketStore,
    UserStore,
} from "../../stores";

import {
    Collaborator,
    Observation,
    Task,
    Ticket,
} from "Stores/dashboardStore/tickets";
import { Dashboard } from "Stores/dashboardStore";
import { User } from "Stores/userStore";
import { Label, LabelGroup, LabelStore } from "Stores/labelStore";
import { SimpleCheckbox } from "../Common/SimpleCheckbox";
import { runInAction, toJS } from "mobx";
import { CustomSelectWrapper } from "Components/Common/CustomSelectWrapper";
import { AttachmentsTable } from "Components/Tickets/AttachmentsTable";
import { TicketHistory } from "Components/Tickets/History";
import { ActionPlanMilestones } from "Components/Tickets/Milestones";
import {
    AttachmentFile,
    AttachmentSelector,
} from "Components/Common/AttachmentSelector";
import {
    IndividualItem,
    StrategicPlanItem,
    StrategicPlanParent,
} from "Stores/strategicPlanStore";
import { TicketViewersList } from "./ViewersList";
import { Info } from "lucide-react";
import { DateInputWithIcon } from "Components/Common/DateInputWithIcon";
import { CommentBadge } from "Components/Common/CommentBadge";
import CloseIcon from "Components/Icons/CloseIcon";
import { SnapshotConfirmModal } from "./ActionPlanModals";
import { TicketSnapshot } from "./Snapshot";
import { ToastContext } from "@/contexts/toastContext";
import { AppContextType } from "@/contexts/app";
import { GroupedMultiSelect, Option as GroupedOptionItem } from "Components/Common/GroupedMultiSelect";

const SUCCESS_UPLOAD_FADE_OUT_MILLIS = 5000;

// Helper component to access toast context
interface CopyButtonProps {
    copyUrl: string;
    className?: string;
    size?: "sm" | "lg";
    children: React.ReactNode;
}

const CopyButton = ({ copyUrl, className, size, children }: CopyButtonProps) => {
    const toast = React.useContext(ToastContext);

    const handleCopy = async () => {
        await navigator.clipboard.writeText(copyUrl);
        toast?.toast({
            type: "success",
            title: "Link Copied",
            message: "Dashboard link has been copied to your clipboard"
        });
    };

    return (
        <Button className={className} size={size} onClick={handleCopy}>
            {children}
        </Button>
    );
};

interface FieldValidationProps {
    isValid: boolean;
    hasInput: boolean;
    message: string;
}

interface FieldValidationState {
    hasReceivedInput: boolean;
}

class FieldValidationMessage extends Component<
    FieldValidationProps,
    FieldValidationState
> {
    state = {
        hasReceivedInput: false,
    };

    static getDerivedStateFromProps(props, state) {
        if (!state.hasReceivedInput && props.hasInput) {
            return { hasReceivedInput: true };
        }
        return null;
    }

    render() {
        return (
            <div className="field-validation">
                {!this.props.hasInput && !this.state.hasReceivedInput ? (
                    "* required"
                ) : this.props.isValid ? (
                    <div>
                        <i className="icon icon-check-circle2" /> required
                    </div>
                ) : (
                    <div>
                        <i className="icon icon-times-circle2 error" />
                        {` ${this.props.message}`}
                    </div>
                )}
            </div>
        );
    }
}

interface ActionplanCloseModalProps {
    ticketStore?: TicketStore;
    attachmentStore?: AttachmentStore;
    snapshotStore?: SnapshotStore;
    onCancel: () => void;
    onSave: (
        finalComment: string,
        goalMet: boolean,
        attachmentId?: string
    ) => void;
    isQuickActionplan: boolean;
    isSaving: boolean;
}

interface ActionplanCloseModalState {
    finalComment: string;
    goalMet: undefined | string;
    attachment: undefined | AttachmentFile;
}

@inject("ticketStore", "attachmentStore", "snapshotStore")
@observer
class ActionplanCloseModal extends Component<
    ActionplanCloseModalProps,
    ActionplanCloseModalState
> {
    filesRef;

    constructor(props) {
        super(props);

        this.state = {
            finalComment: "",
            goalMet: undefined,
            attachment: undefined,
        };
    }

    handleEnter = () => {};

    handleCommentChange = (ev: any) => {
        this.setState({ finalComment: ev.target.value });
    };

    handleGoalMetChange = (ev: ChangeEvent<HTMLInputElement>) => {
        this.setState({ goalMet: ev.target.value });
    };

    handleAttachmentChange = (ev) => {
        ev.preventDefault();
        const attachment = this.filesRef!.files[0];
        attachment.sanitizedName = Utils.sanitizeFileName(attachment.name);
        this.setState({ attachment });
    };

    handleAttachmentClear = () => {
        this.setState({ attachment: undefined });
    };

    handleCloseActionplan = () => {
        const goalMet = this.state.goalMet!.toLowerCase() === "true";

        if (this.state.attachment !== undefined) {
            this.props
                .attachmentStore!.createAndUploadAttachments([
                this.state.attachment!,
            ])
                .then((resp) => {
                    this.props.onSave(
                        this.state.finalComment,
                        goalMet,
                        resp[0]
                    );
                });
        } else {
            this.props.onSave(this.state.finalComment, goalMet);
        }
    };

    showAttachmentSelector = () => {
        this.filesRef!.click();
    };

    assignFileRef = (ref) => (this.filesRef = ref);

    render() {
        return (
            <Modal onEntered={this.handleEnter} show={true} centered>
                <Modal.Body>
                    <div className="header edit">CLOSE ACTIONPLAN</div>
                    <div style={{ fontSize: 16, paddingBottom: 10 }}>
                        Before you close this ActionPlan, please leave a comment
                        and verify if target was met.
                    </div>
                    <div className="target-goal" id="ticketStatusUpdate">
                        <Form.Group>
                            <Form.Label>Final Comment</Form.Label>
                            <Form.Control
                                as={"textarea"}
                                onChange={this.handleCommentChange}
                            />
                        </Form.Group>

                        <div className="closing-attachment-selector">
                            <span
                                className="link"
                                onClick={this.showAttachmentSelector}
                            >
                                Upload an image
                            </span>
                            &nbsp;
                            {this.props.isQuickActionplan
                                ? "to use for the final snapshot (optional)"
                                : "to use in place of a dashboard snapshot (optional)"}
                            <input
                                style={{ display: "none" }}
                                type="file"
                                accept="image/*"
                                onChange={this.handleAttachmentChange}
                                ref={this.assignFileRef}
                            />
                            {this.state.attachment === undefined ? null : (
                                <div className="item-chip">
                                    {
                                        (
                                            this.state
                                                .attachment! as AttachmentFile
                                        ).sanitizedName
                                    }{" "}
                                    <i
                                        className="icon icon-times"
                                        onClick={this.handleAttachmentClear}
                                    />
                                </div>
                            )}
                        </div>
                        <div>
                            <p>Was the target for closure achieved?</p>
                            <Form.Check
                                inline
                                label="yes"
                                value="true"
                                checked={this.state.goalMet === "true"}
                                name="measurable-target-goal-met-radio"
                                type={"radio"}
                                onChange={this.handleGoalMetChange}
                            />
                            <Form.Check
                                inline
                                label="no"
                                value="false"
                                checked={this.state.goalMet === "false"}
                                name="measurable-target-goal-met-radio"
                                onChange={this.handleGoalMetChange}
                                type={"radio"}
                            />
                        </div>
                        <div className="text-end">
                            <Button
                                variant="secondary"
                                size="lg"
                                onClick={this.props.onCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                size="lg"
                                style={{ marginLeft: 15 }}
                                disabled={
                                    !this.state.finalComment ||
                                    this.state.finalComment === "" ||
                                    this.state.goalMet === undefined
                                }
                                onClick={this.handleCloseActionplan}
                            >
                                {this.props.ticketStore!.isProcessing ||
                                this.props.attachmentStore!.isUploading ||
                                this.props.isSaving ? (
                                    <Spinner animation="border" />
                                ) : (
                                    "Save"
                                )}
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        );
    }
}

const TAB_KEY_BY_NAME: Record<string, string> = {
    details: "1",
    comments: "2",
    milestones: "3",
    attachments: "4",
    history: "5",
};

export interface DetailFormProps {
    commonStore?: CommonStore
    labelStore?: LabelStore
    ticketStore?: TicketStore
    attachmentStore?: AttachmentStore
    commentStore?: CommentStore
    notificationViewerStore?: NotificationViewerStore
    userStore?: UserStore
    appStore?: AppStore
    strategicPlanStore?: StrategicPlanStore
    snapshotStore?: SnapshotStore
    historyStore?: HistoryStore
    milestoneStore?: MilestoneStore
    ticket?: Task | Observation
    dashboard?: Dashboard
    currentTicket?: Ticket
    refreshView?: string | null
    isPreview: boolean
    shouldShowSnapshotWarning?: boolean
    setFollowUpTo?: Partial<Task>
    onSaveDone?: (ruuid?: string, isUpdate?: boolean) => void;
    onCancel?: () => void
    onCloseDone?: () => void
    onUpdate?: () => void
    formRef?: any
    appContext?: AppContextType
    initialTab?: string
}

interface DetailFormState {
    [idx: string]:
        | { [idx: string]: Label[] | undefined }
        | AttachmentFile[]
        | Collaborator[]
        | Committee
        | Committee[]
        | Course
        | User
        | StrategicPlanItem
        | StrategicPlanParent
        | Student
        | Student[]
        | string
        | boolean
        | number
        | undefined
        | null
        | IndividualItem[];
    formIsValid: boolean;
    type: "observation" | "task";
    allowAssigneeToChangeDueDate: boolean;
    committees: Committee[];
    course: Course | undefined;
    assignee: User | undefined;
    // Object where key is group id and value is either Label or undefined
    labels: { [idx: string]: Label[] | undefined };
    collaborators: any[] | undefined;
    students: Student[];
    studentsIsMulti: boolean;
    target?: string;
    objective?: string;
    progress?: string;
    ddd?: string;
    dashboardInterpretation?: string;
    dashboardId?: number;
    refreshView?: string | null;
    evaluationDate?: string | undefined;
    final_comment?: string;
    showCommentForm: boolean;
    showCloseConfirm: boolean;
    showSnapshotConfirm: boolean;
    goal_met?: boolean;
    insightHasDoneBlur: boolean;
    attachments: AttachmentFile[];
    strategicPlanItems: IndividualItem[];
    strategicPlan: StrategicPlanParent | undefined | null;
    showAttachmentAddedMessage: boolean;
    isSaving: boolean;
    templateSelection: any | undefined;
    reasonDueDateText: string | undefined;
    oldDueDate?: string | undefined;
}

@inject(
    "ticketStore",
    "attachmentStore",
    "commonStore",
    "labelStore",
    "userStore",
    "commentStore",
    "appStore",
    "strategicPlanStore",
    "snapshotStore",
    "historyStore",
    "milestoneStore",
    "notificationViewerStore"
)
@observer
export class TicketDetailForm extends Component<
    DetailFormProps,
    DetailFormState
> {
    static contextType = ToastContext;
    declare context: React.ContextType<typeof ToastContext>;

    formRef;
    attachmentTableRef;
    showAttachmentAddedTimeout;

    getFollowUpText = (task: Partial<Task>) => {
        return (
            "This is a follow up to closed action #" +
            task.task_id_display +
            " with id " +
            task.ruuid +
            ".\n\n The proposed action was: `" +
            task.ddd +
            "`.\n\n The goal was: `" +
            task.objective +
            "`.\n\n"
        );
    };

    state: DetailFormState = {
        formIsValid: false,
        showCommentForm: false,
        showCloseConfirm: false,
        showSnapshotConfirm: false,
        insightHasDoneBlur: false,
        type: this.props.currentTicket
            ? (this.props.currentTicket.type as "observation" | "task")
            : "observation",
        allowAssigneeToChangeDueDate: this.props.currentTicket
            ? (this.props.currentTicket.assignee_change_due_date as boolean)
            : false,
        committees: [],
        course: undefined,
        labels: {},
        assignee: undefined,
        collaborators: [],
        students: [],
        studentsIsMulti: true,
        dashboardInterpretation: this.props.currentTicket
            ? this.props.currentTicket.dashboard_interpretation
            : this.props.setFollowUpTo
                ? this.getFollowUpText(this.props.setFollowUpTo)
                : "",
        dashboardId: this.props.currentTicket
            ? (this.props.currentTicket.dashboard_id as number)
            : this.props.dashboard
                ? (this.props.dashboard.id as number)
                : undefined,
        target:
            this.props.currentTicket && this.props.currentTicket.target
                ? (this.props.currentTicket.target as string)
                : undefined,
        progress:
            this.props.currentTicket && this.props.currentTicket.progress
                ? (this.props.currentTicket.progress as string)
                : undefined,
        objective:
            this.props.currentTicket && this.props.currentTicket.objective
                ? (this.props.currentTicket.objective as string)
                : undefined,
        ddd:
            this.props.currentTicket && this.props.currentTicket.ddd
                ? (this.props.currentTicket.ddd as string)
                : undefined,
        evaluationDate:
            this.props.currentTicket && this.props.currentTicket.evaluation_date
                ? Utils.getValidDateFromString(
                    this.props.currentTicket.evaluation_date! as string,
                    "YYYY-MM-DD HH:mm:ss"
                )
                : undefined,
        attachments: [],
        strategicPlanItems: [],
        strategicPlan: undefined,
        showAttachmentAddedMessage: false,
        isSaving: false,
        templateSelection: undefined,
        oldDueDate:
            this.props.currentTicket && this.props.currentTicket.evaluation_date
                ? Utils.getValidDateFromString(
                    this.props.currentTicket.evaluation_date! as string,
                    "YYYY-MM-DD HH:mm:ss"
                )
                : undefined,
        reasonDueDateText: undefined,
        refreshView: this.props.refreshView,
    };

    constructor(props) {
        super(props);
        this.formRef = createRef();
    }

    componentDidMount() {
        if (
            !this.props.currentTicket &&
            this.props.ticketStore &&
            !this.props.ticketStore.allTickets
        ) {
            this.props.ticketStore.fetchTickets(false);
        }

        runInAction(() => {
            if (this.props.commentStore) {
                this.props.commentStore.comments = [];
            }
            if (this.props.attachmentStore) {
                this.props.attachmentStore.attachments = [];
            }
            if (this.props.milestoneStore) {
                this.props.milestoneStore.milestones = [];
            }
        });

        this.props.labelStore!.fetchLabelGroups().then(() => {
            const groups = toJS(this.props.labelStore!.labelGroups);
            if (this.props.currentTicket) {
                const selectedLabels = toJS(this.props.currentTicket.labels);
                const selected = groups.reduce(
                    (prev, cur: LabelGroup) =>
                        Object.assign({}, prev, {
                            [cur.id]: selectedLabels[cur.id]
                                ? Utils.humanSort(
                                    selectedLabels[cur.id],
                                    (label: Label) => label.name
                                )
                                : undefined,
                        }),
                    {}
                );
                this.setState({ labels: selected });
                this.props.notificationViewerStore?.fetchUserViewersNotification(
                    this.props?.currentTicket?.ruuid as string
                );
                this.props.notificationViewerStore?.setPageIndex(0);
            } else {
                const selected = groups.reduce(
                    (prev, cur: LabelGroup) =>
                        Object.assign({}, prev, {
                            [cur.id]: undefined,
                        }),
                    {}
                );
                this.setState({ labels: selected });
            }
        });

        if (this.props.isPreview) {
            Utils.pushViewName("Preview ActionPlan");
        } else if (this.props.currentTicket) {
            Utils.pushViewName("Edit ActionPlan");
        } else {
            Utils.pushViewName("Create ActionPlan");
        }

        if (
            this.props.commonStore &&
            this.props.labelStore &&
            this.props.userStore &&
            this.props.strategicPlanStore
        ) {
            Promise.all([
                this.props.commonStore.fetchCourses("basic"),
                this.props.commonStore.fetchCommittees(),
                this.props.commonStore.fetchStudents({
                    dashboardId: this.state.dashboardId,
                }),
                this.props.commonStore.fetchDashboardViews(
                    this.state.dashboardId,
                    this.props.currentTicket?.ruuid
                ),
                this.props.userStore.fetchAssigneeList(),
                this.props.strategicPlanStore.fetchStrategicPlanItemsOnly(),
                this.props.strategicPlanStore.fetchStrategicPlanItems(true),
            ]).then(() => {
                if (this.props.currentTicket) {
                    const ticket = toJS(this.props.currentTicket!);
                    const course = this.props.commonStore!.courses
                        ? this.props.commonStore!.courses.find(
                            (c: Course) => c.id === ticket.course_id
                        )
                        : undefined;
                    const allCommittees = this.props.commonStore!.committees;
                    let committees: Committee[] = [];
                    if (allCommittees) {
                        if (ticket.committees && ticket.committees.length > 0) {
                            const ticketCommitteeIds = new Set(
                                ticket.committees.map(c => c.id)
                            );
                            committees = allCommittees.filter((c: Committee) =>
                                ticketCommitteeIds.has(c.id)
                            );
                        } else if (ticket.committee_id) {
                            const legacy = allCommittees.find(
                                (c: Committee) => c.id === ticket.committee_id
                            );
                            if (legacy) committees = [legacy];
                        }
                    }
                    const assignee = this.props.userStore!.assignees
                        ? this.props.userStore!.assignees.find(
                            (u: User) => u.ruuid === ticket.assignee_ruuid
                        )
                        : undefined;
                    const collaborators = toJS(
                        this.props.currentTicket!.collaborators
                    ).map((col: Collaborator) =>
                        Object.assign({}, col, { ruuid: col.faculty_ruuid })
                    );
                    const strategicPlanItems =
                        this.props.currentTicket.strategic_plan_items.map(
                            (item) => {
                                const individualItem: IndividualItem = {
                                    id: item.id as number,
                                    name: item.name,
                                    school_id: item.school_id,
                                    status: item.status,
                                    parent_id: item.parent_id,
                                    strategic_plan_ruuid:
                                    item.strategic_plan_ruuid,
                                    created: item.created,
                                    updated: item.updated,
                                    level: item.level
                                        ? item.level.toString()
                                        : "0",
                                    strategic_plan_name: item.name,
                                };
                                return individualItem;
                            }
                        );
                    const itemParent =
                        this.props.strategicPlanStore!.individualItems.find(
                            (item) =>
                                strategicPlanItems.length > 0 &&
                                item.id === strategicPlanItems[0].id
                        );
                    const strategicPlan = itemParent
                        ? this.props.strategicPlanStore!.strategicPlanItems.find(
                            (item) =>
                                item.ruuid ===
                                itemParent!.strategic_plan_ruuid
                        )
                        : undefined;
                    let students: Student[] = [];
                    let studentsIsMulti = true;
                    // Always use array format for students to support multi-selection
                    if (
                        this.props.currentTicket.students.length > 0 &&
                        this.props.commonStore!.students
                    ) {
                        students = this.props.currentTicket.students.map((ticketStudent: any) => {
                            const foundStudent = this.props.commonStore!.students.find(
                                (student) => student.ruuid === ticketStudent.ruuid
                            );
                            return foundStudent || ticketStudent;
                        });
                    }
                    this.setState({
                        course,
                        committees,
                        assignee,
                        collaborators,
                        students,
                        studentsIsMulti,
                        strategicPlanItems,
                        strategicPlan,
                    });
                }
            });
        }
        this.updateCommentsAndAttachments();
        this.updateMilestones();
    }

    componentWillUnmount() {
        Utils.popViewName();
    }


    toIndividualItems = (items: StrategicPlanItem[]): IndividualItem[] => {
        return items.map((item) => {
            const individualItem: IndividualItem = {
                id: item.id as number,
                name: item.name,
                school_id: item.school_id,
                status: item.status,
                parent_id: item.parent_id,
                strategic_plan_ruuid: item.strategic_plan_ruuid,
                created: item.created,
                updated: item.updated,
                level: item.level ? item.level.toString() : "0",
                strategic_plan_name: item.name,
            };
            return individualItem;
        });
    };

    isValid = () => {
        const {
            assignee,
            evaluationDate: evalDate,
            reasonDueDateText,
        } = this.state;
        const userChosenSchoolInfo = Utils.getChosenSchoolInfo();
        // Basic validation for task and observation, if observation due date is not set
        // If task due date is required.
        const isValid =
            (assignee &&
                this.isInterpretationValid() &&
                this.isDueDateValid()) ||
            (assignee &&
                this.isInterpretationValid() &&
                this.state.type === "observation");

        // If strategic plan is selected, then it is required to have at least one item selected.
        if (
            this.state.strategicPlan &&
            this.state.strategicPlanItems.length === 0
        ) {
            return false;
        }
        if (
            this.state.type === "task" ||
            (this.props.currentTicket &&
                this.props.currentTicket.type === "task")
        ) {
            if (
                !userChosenSchoolInfo.is_admin &&
                this.props.currentTicket !== undefined
            ) {
                return isValid && evalDate !== undefined;
            } else if (
                this.isValidReasonDueDateChange() &&
                this.props.currentTicket !== undefined
            ) {
                return (
                    isValid &&
                    reasonDueDateText !== undefined &&
                    reasonDueDateText !== ""
                );
            } else {
                return isValid;
            }
        } else if (!this.props.currentTicket) {
            return !!isValid;
        } else if (this.isValidReasonDueDateChange()) {
            return (
                isValid &&
                reasonDueDateText !== undefined &&
                reasonDueDateText !== ""
            );
        } else {
            return isValid;
        }
    };

    shouldDisableField = (field: string): boolean => {
        return (
            this.props.isPreview ||
            (!!this.props.currentTicket &&
                !!this.props.currentTicket.permissions &&
                !this.props.currentTicket.permissions[field])
        );
    };

    isInterpretationValid = (): boolean => {
        const { dashboardInterpretation: dbInter } = this.state;
        return !!dbInter && dbInter.trim().length > 3;
    };

    isDueDateValid = () => {
        // ActionPlans (tasks) should be valid regardless of whether they have a due date
        // This allows users to create and close ActionPlans without requiring due dates
        return true;
    };

    handleSave = async () => {
        const userChosenSchoolInfo = Utils.getChosenSchoolInfo();
        this.setState({ isSaving: true });
        try {
            // Set isProcessing to true so that the spinner shows while uploading attachments
            runInAction(() => {
                this.props.ticketStore!.isProcessing = true;
            });

            // Convert to date.  Do not convert to utc. Notifications will be run local to customer time.
            const evalDate = this.state.evaluationDate
                ? Utils.formatDate(
                    new Date(this.state.evaluationDate as string),
                    "YYYY-MM-DD"
                )
                : null;
            if (
                this.state.type === "task" &&
                !evalDate &&
                !userChosenSchoolInfo.is_admin
            ) {
                if (this.context && this.context.toast) {
                    if (this.context && this.context.toast) {
                        this.context.toast({
                            type: "error",
                            title: "Missing Evaluation Date",
                            message: "An evaluation date is required.",
                            duration: 5000,
                        });
                    }
                }
                return;
            }

            const dashboardParams = this.props.currentTicket
                ? this.props.currentTicket.dashboard_params
                : {};

            const groups = toJS(this.props.labelStore!.labelGroups);
            const labels = groups.reduce(
                (prev, cur: LabelGroup) =>
                    this.state.labels[cur.id]
                        ? Object.assign({}, prev, {
                            [cur.id]: this.state.labels[cur.id],
                        })
                        : prev,
                {}
            );

            let attachments = [];
            if (this.state.attachments) {
                attachments =
                    await this.props.attachmentStore!.createAndUploadAttachments(
                        this.state.attachments
                    );
            }

            let students: any[] = [];
            // Always handle students as array since we now support multi-selection
            if (this.state.students && Array.isArray(this.state.students)) {
                students = (this.state.students as Student[]).map(
                    ({ ruuid }) => ({ enflux_student_ruuid: ruuid })
                );
            }

            const toSend = {
                ruuid: this.props.currentTicket
                    ? this.props.currentTicket.ruuid
                    : undefined,
                name: this.state.dashboardInterpretation as string,
                type: this.state.type,
                assignee_ruuid: this.state.assignee!.ruuid,
                collaborators: this.state.collaborators
                    ? this.state.collaborators.map((u: any) => ({
                        faculty_ruuid: u.ruuid,
                    }))
                    : [],
                dashboard_params: JSON.stringify(dashboardParams),
                dashboard_id: this.state.dashboardId,
                attachments: attachments,
                labels: labels,
                students: students,
                course_id: this.state.course ? this.state.course.id : 0,
                committee_ids: this.state.committees.map(c => c.id),
                dashboard_interpretation: this.state.dashboardInterpretation,
                target: this.state.target,
                progress: this.state.progress,
                objective: this.state.objective,
                ddd: this.state.ddd,
                strategic_plan_items: this.state.strategicPlanItems.map(
                    (s: IndividualItem) => s.id
                ),
                reason_for_date_change: this.state.reasonDueDateText
                    ? this.state.reasonDueDateText
                    : undefined,
            };

            if (
                !this.props.currentTicket &&
                this.props.dashboard !== undefined &&
                this.props.dashboard.dashboard_type === "enflux_dashboard"
            ) {
                const { url, attachmentFile } =
                    await Utils.getSnapshotAttachmentFileAndUrl(
                        this.props.snapshotStore!
                    );

                if (attachmentFile !== undefined) {
                    const initialAttachment =
                        await this.props.attachmentStore!.createAndUploadAttachments(
                            [attachmentFile]
                        );
                    Object.assign(toSend, {
                        initial_attachment_id: initialAttachment[0],
                    });
                }

                Object.assign(toSend, { dashboard_image_url: url });
            }

            Object.assign(toSend, {
                evaluation_date: evalDate,
                assignee_change_due_date:
                this.state.allowAssigneeToChangeDueDate,
            });

            if (this.state.type === "task") {
                if (!this.props.currentTicket) {
                    // If creating a new actionable ActionPlan, include status of "open"
                    Object.assign(toSend, { status: "open" });
                }
            }
            await this.props.ticketStore!.createOrUpdateTicket(toSend, Utils.getChosenSchoolInfo());
            if(this.props.onSaveDone){
                // If a new ticket was created, pass its ruuid
                const ruuid = this.props.currentTicket ? this.props.currentTicket.ruuid : (this.props.ticketStore!.ticket && this.props.ticketStore!.ticket.ruuid);
                this.props.onSaveDone(ruuid, !!this.props.currentTicket);
            }
            this.setState({attachments: []});
            this.props.snapshotStore!.resetSnapshot();
        } catch (error) {
            // Show error to user
            if (this.props.appStore) {
                this.props.appStore.addError(error);
            } else {
                if (this.context && this.context.toast) {
                    this.context.toast({
                        type: "error",
                        title: "Error",
                        message: error?.message || "An error occurred while saving the ActionPlan.",
                        duration: 5000,
                    });
                }
            }
        } finally {
            this.setState({ isSaving: false });
            runInAction(() => {
                this.props.ticketStore!.isProcessing = false;
            });
        }
    };

    updateCommentsAndAttachments = () => {
        if (
            this.props.commentStore &&
            this.props.currentTicket &&
            this.props.currentTicket.ruuid
        ) {
            this.props.commentStore.fetchCommentsForTicket(
                this.props.currentTicket.ruuid
            );
        }

        if (this.props.currentTicket) {
            this.props.attachmentStore!.getAllAttachmentsForActionplan(
                this.props.currentTicket.ruuid,
                true
            );
        }
    };

    getCompleteMilestones = () => {
        if (this.props.currentTicket && this.props.milestoneStore) {
            return this.props.milestoneStore.milestones.reduce((acc, curr) => {
                if (curr.status === MilestoneStatus.COMPLETE) {
                    return acc + 1;
                }
                return acc;
            }, 0);
        }
        return 0;
    };

    hasIncompleteMilestones = () => {
        if (
            this.props.currentTicket &&
            this.props.milestoneStore &&
            this.props.milestoneStore.milestones.length > 0
        ) {
            const incomplete = this.props.milestoneStore.milestones.filter(
                (milestone) => {
                    return milestone.status !== MilestoneStatus.COMPLETE;
                }
            );
            return incomplete.length > 0;
        }
        return false;
    };

    updateMilestones = () => {
        if (this.props.currentTicket && this.props.milestoneStore) {
            this.props.milestoneStore.getAllMilestonesForActionPlan(
                this.props.currentTicket.ruuid
            );
        }
    };

    handleSnapshotUpdate = () => {
        this.updateCommentsAndAttachments();
        if (this.props.onUpdate) {
            this.props.onUpdate();
        }
    };

    toggleTaskStatus = () => {
        if (!this.props.currentTicket) {
            return;
        }

        // For observations, status is null. For tasks without due dates, status is also null.
        // We treat null and "open" the same way - both can be closed.
        const isNotClosed = this.props.currentTicket.status !== "closed";
        
        if (isNotClosed) {
            const hasIncompleteMilestones = this.hasIncompleteMilestones();
            const proceedWithClose = () => {
                const isOnActionPlansPage = window.location.pathname.includes("/actionplans");
                if (isOnActionPlansPage && this.props.currentTicket?.dashboard_id) {
                    this.setState({
                        showSnapshotConfirm: true,
                    });
                } else {
                    this.setState({
                        showCloseConfirm: true,
                    });
                }
            };

            if (!hasIncompleteMilestones) {
                proceedWithClose();
            } else {
                this.props.appStore!.showConfirmModal({
                    element: (
                        <p>
                            This ActionPlan has milestones that have not been
                            completed. Do you want to continue or do you want to
                            cancel closing the ActionPlan for now?
                        </p>
                    ),
                    accept: () => {
                        proceedWithClose();
                    },
                    cancel: () => {
                        this.setState({ showFormForId: undefined });
                    },
                    showActions: true,
                });
            }
        } else {
            if (!this.props.appStore) return;
            this.props.appStore.showConfirmModal({
                element: (
                    <div>Are you sure you want to re-open this ActionPlan?</div>
                ),
                accept: () => {
                    this.setState({ status: "open" }, async () => {
                        if (!this.props.ticketStore) return;
                        let url: string | null = null;
                        if (this.props.currentTicket && this.props.dashboard) {
                            const { url: newUrl } =
                                await Utils.getSnapshotAttachmentFileAndUrl(
                                    this.props.snapshotStore!
                                );
                            url = newUrl;
                        }

                        this.props.ticketStore
                            .createOrUpdateTicket(
                                {
                                    ruuid: this.props.currentTicket
                                        ? this.props.currentTicket.ruuid
                                        : undefined,
                                    status: "open",
                                    dashboard_id: this.state.dashboardId,
                                    closed_dashboard_image_url: "",
                                    snapshot_image_url: url,
                                },
                                Utils.getChosenSchoolInfo()
                            )
                            .then(() => {
                                if (this.props.dashboard) {
                                    return this.props.ticketStore!.getTicketsForDashboard(
                                        this.props.dashboard.id
                                    );
                                }
                                return this.props.ticketStore!.fetchTickets(
                                    false
                                );
                            })
                            .then(() => {
                                if (this.props.currentTicket) {
                                    this.props.historyStore!.getAllHistoryForActionPlan(
                                        this.props.currentTicket.ruuid
                                    );
                                }
                                if (this.props.onUpdate) {
                                    this.props.onUpdate();
                                }
                                if (this.props.onCloseDone) {
                                    this.props.onCloseDone();
                                }
                            });
                    });
                },
                cancel: () => this.setState({ showFormForId: undefined }),
                showActions: true,
            });
        }
    };

    closeModal = () => {
        this.setState({
            showCloseConfirm: false,
            showSnapshotConfirm: false,
            isSaving: false,
        });
    };

    handleCloseWithSnapshot = () => {
        const ticket = this.props.currentTicket!;
        this.setState({
            showSnapshotConfirm: false,
        });

        if (ticket.dashboard_id) {
            const dashboardUrl = Utils.getActionplanUrl(
                ticket.dashboard,
                ticket.ruuid
            );
            window.location.href = `${dashboardUrl}?closeActionPlan=${ticket.ruuid}`;
        }
    };

    handleCloseWithoutSnapshot = () => {
        this.setState({
            showSnapshotConfirm: false,
            showCloseConfirm: true,
        });
    };

    handleLabelSelect = (group: number, nextVal: any) => {
        const sorted = nextVal
            ? Utils.humanSort(nextVal, (label: Label) => label.name)
            : nextVal;
        const labels = Object.assign({}, this.state.labels, {
            [group]: sorted,
        });
        this.setState({ labels });
    };

    renderLabelDropdowns = () => {
        if (!this.props.labelStore || !this.props.labelStore.labelGroups) {
            return <Spinner animation="border" />;
        }

        const groups = toJS(this.props.labelStore.labelGroups) as LabelGroup[];
        const disabled = this.shouldDisableField("can_edit_labels");

        // Build grouped options for the new component, excluding archived labels
        const groupedOptions = groups
            .filter((g) => g.labels && g.labels.length > 0)
            .map((g) => ({
                label: g.name,
                options: g.labels
                    .filter((l) => l.is_archived === false)
                    .map((l) => ({ value: String(l.id), label: l.name })),
            }));

        // Build a flat selected options array from existing state shape (groupId -> Label[])
        const selectedFlat: GroupedOptionItem[] = Object.entries(this.state.labels || {})
            .flatMap(([groupId, labels]) => (labels || []).map((l: any) => ({ value: String(l.id), label: l.name })));

        // Map of labelId -> { groupId, label }
        const labelIdToGroupAndLabel = new Map<number, { groupId: number; label: any }>();
        groups.forEach((g) => {
            g.labels.forEach((l) => {
                labelIdToGroupAndLabel.set(l.id, { groupId: g.id, label: l });
            });
        });

        const handleGroupedChange = (next: GroupedOptionItem[]) => {
            // Group selected options back by label group id
            const grouped = groups.reduce((acc, g) => {
                acc[g.id] = undefined as any; // ensure all groups exist to mirror previous shape
                return acc;
            }, {} as Record<string, any[]>);

            next.forEach((opt) => {
                const idNum = Number(opt.value);
                const found = labelIdToGroupAndLabel.get(idNum);
                if (found) {
                    const cur = grouped[found.groupId] || [];
                    cur.push(found.label);
                    grouped[found.groupId] = cur;
                }
            });

            // Sort each group's labels by name for consistency
            Object.keys(grouped).forEach((k) => {
                if (grouped[Number(k)]) {
                    grouped[Number(k)] = Utils.humanSort(grouped[Number(k)], (label: any) => label.name);
                }
            });

            this.setState({ labels: grouped });
        };

        const content = (
            <GroupedMultiSelect
                label={"Labels"}
                options={groupedOptions}
                value={selectedFlat}
                onChange={handleGroupedChange}
                placeholder="Select labels"
            />
        );

        return disabled ? (
            <div style={{ pointerEvents: "none", opacity: 0.7 }}>{content}</div>
        ) : (
            content
        );
    };

    handleActionPlanEdit = () => {};

    handleInsightChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ dashboardInterpretation: ev.currentTarget.value });
    };

    handleDDDChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ ddd: ev.currentTarget.value });
    };

    handleObjectiveChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ objective: ev.currentTarget.value });
    };

    handleGoalChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ target: ev.currentTarget.value });
    };

    handleProgressChange = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ progress: ev.currentTarget.value });
    };

    handleAssigneeChange = (option: any) => {
        this.setState({ assignee: option });
    };

    getAssigneeRenderer = (option: any) => (
        <div className="assignee-option" key={option.value as string}>
            {option.name}
            <br />
            <span>{option.email}</span>
        </div>
    );

    handleCommitteeChange = (option: any) => {
        this.setState({ committee: option });
    };

    handleSelectDate = (d: Date | null) => {
        if (d && !isNaN(d.getTime())) {
            try {
                const evaluationDate = Utils.changeDatePickerTimeStampToEOD(d);
                this.setState({ evaluationDate, type: "task" });
            } catch (error) {
                console.error('Error setting evaluation date:', error);
                if (this.context?.toast) {
                    this.context.toast({
                        type: "error",
                        title: "Invalid Date",
                        message: "Please enter a valid date in MM/DD/YYYY format"
                    });
                }
            }
        } else {
            this.setState({ evaluationDate: undefined, type: "observation" });
        }
    };

    handleManualDateInput = (dateString: string) => {
        // Handle undefined/null values that can occur when clicking calendar days
        if (!dateString || !dateString.trim()) {
            this.handleSelectDate(null);
            return;
        }

        const parsedDate = Utils.parseUserDateInput(dateString);
        
        if (parsedDate) {
            this.handleSelectDate(parsedDate);
        } else {
            if (this.context?.toast) {
                this.context.toast({
                    type: "error",
                    title: "Invalid Date Format",
                    message: "Please enter a date in MM/DD/YYYY format (e.g., 1/15/2027)"
                });
            }
        }
    };

    handleClassificationChange = (option: any) => {
        this.setState({ course: option });
    };

    handleInsightBlur = () => {
        if (this.state.dashboardInterpretation) {
            this.setState({ insightHasDoneBlur: true });
        }
    };

    handleCollaboratorsChange = (next: any) => {
        this.setState({ collaborators: next });
    };

    handleCommitteesChange = (next: any) => {
        this.setState({ committees: next || [] });
    };

    handleStudentsChange = (next: any) => {
        this.setState({ students: next });
    };
    handleActionableToggle = (checked: boolean) => {
        if (checked) {
            this.setState({ type: "task" });
        } else {
            this.setState({
                type: "observation",
                evaluationDate: undefined,
                allowAssigneeToChangeDueDate: false,
            });
        }
    };

    handleAllowAssigneeToChangeDueDate = (checked: boolean) => {
        this.setState({ allowAssigneeToChangeDueDate: checked });
    };

    fetchCurrentActionplanEntities = (callback?: () => void) => {
        Promise.all([
            this.props.attachmentStore!.getAllAttachmentsForActionplan(
                this.props.currentTicket!.ruuid,
                true
            ),
            this.props.commentStore!.fetchCommentsForTicket(
                this.props.currentTicket!.ruuid
            ),
        ]).then(callback);
    };

    handleDeleteComment = (commentId: string) => {
        this.props.commentStore!.deleteComment(commentId).then(() => {
            this.fetchCurrentActionplanEntities();
        });
    };

    handleDeleteAttachment = (attachmentId: string) => {
        this.props.attachmentStore!.deleteAttachment(attachmentId).then(() => {
            this.fetchCurrentActionplanEntities();
        });
    };

    handleCommentAttachmentDelete = (
        attachmentId: string,
        commentId: string
    ) => {
        this.props
            .attachmentStore!.deleteAttachmentFromComment(
            commentId,
            attachmentId
        )
            .then(() => {
                this.fetchCurrentActionplanEntities();
            });
    };

    handleAttachmentChange = (attachments: AttachmentFile[]) => {
        this.setState({ attachments });
    };

    handleCloseActionplan = async (
        finalComment: string,
        goalMet: boolean,
        attachmentId?: string
    ) => {
        this.setState({ isSaving: true }, async () => {
            const actionplanData: Partial<Ticket> = {
                ruuid: this.props.currentTicket
                    ? this.props.currentTicket.ruuid
                    : undefined,
                status: "closed",
                target_met: goalMet,
                dashboard_id: this.state.dashboardId,
            };
            const commentData: any = {
                comment: finalComment,
                created_by: Utils.getCurrentUser()!.name || undefined,
                attachments: [],
            };

            let skipSnapshotGeneration = false;
            let closingAttachmentId: string | undefined = undefined;
            if (this.props.dashboard !== undefined) {
                const { url, attachmentFile } =
                    await Utils.getSnapshotAttachmentFileAndUrl(
                        this.props.snapshotStore!,
                        true
                    );

                if (attachmentFile !== undefined) {
                    const snapshotAttachmentId = (
                        await this.props.attachmentStore!.createAndUploadAttachments(
                            [attachmentFile]
                        )
                    )[0];

                    // Let API know that snapshot has already been generated so it can skip generation using the custom view url
                    skipSnapshotGeneration = true;

                    commentData.attachments.push(snapshotAttachmentId);
                    closingAttachmentId = snapshotAttachmentId;
                }

                actionplanData.closed_dashboard_image_url = url;
                commentData.dashboard_image_url = url;
            }

            if (attachmentId) {
                commentData.attachments.push(attachmentId);
                // If user has selected an image to use in place of the dashboard snapshot, overwrite closingAttachmentId with their image's attachment id
                closingAttachmentId = attachmentId;
            }

            actionplanData.closed_attachment_id = closingAttachmentId;
            commentData.closed_attachment_id = closingAttachmentId;

            // Update ActionPlan
            this.props
                .ticketStore!.createOrUpdateTicket(
                actionplanData,
                Utils.getChosenSchoolInfo()
            )
                .then(() => {
                    this.props.snapshotStore!.resetSnapshot();
                    // Create comment
                    this.props
                        .ticketStore!.addCommentToTicket(
                        this.props.currentTicket!.ruuid,
                        commentData,
                        skipSnapshotGeneration
                    )
                        .then(() => {
                            // Update list of ActionPlans
                            (this.props.dashboard
                                    ? this.props.ticketStore!.getTicketsForDashboard(
                                        this.props.dashboard.id
                                    )
                                    : this.props.ticketStore!.fetchTickets(false)
                            ).then(() => {
                                this.updateCommentsAndAttachments();
                                if (
                                    this.props.dashboard &&
                                    this.props.dashboard.id &&
                                    this.props.commonStore
                                ) {
                                    this.props.commonStore.fetchDashboardViews(
                                        this.state.dashboardId,
                                        this.props.currentTicket?.ruuid
                                    );
                                }
                                // reload ActionPlan history
                                if (actionplanData.ruuid) {
                                    this.props.historyStore!.getAllHistoryForActionPlan(
                                        actionplanData.ruuid
                                    );
                                }
                                this.closeModal();
                                if (this.props.onUpdate) {
                                    this.props.onUpdate();
                                }
                                if (this.props.onCloseDone) {
                                    this.props.onCloseDone();
                                }
                            });
                        });
                });
        });
    };

    handleDirectAttachmentChange = (files: AttachmentFile[]) => {
        if (!this.props.currentTicket) {
            return;
        }
        // When adding files via the attachment list tab, immediately upload and add the attachments to the ActionPlan
        this.props
            .attachmentStore!.createAndUploadAttachments(files)
            .then((attachmentIdList) => {
                this.props
                    .attachmentStore!.addAttachmentsToActionplan(
                    this.props.currentTicket!.ruuid,
                    attachmentIdList
                )
                    .then(() => {
                        this.fetchCurrentActionplanEntities(() => {
                            this.setState(
                                { showAttachmentAddedMessage: true },
                                () => {
                                    this.showAttachmentAddedTimeout =
                                        setTimeout(() => {
                                            this.setState({
                                                showAttachmentAddedMessage:
                                                    false,
                                            });
                                        }, SUCCESS_UPLOAD_FADE_OUT_MILLIS);
                                }
                            );
                        });
                    });
            });
    };

    handleRef = (el) => (this.formRef = el);

    assignAttachmentTableRef = (ref) => (this.attachmentTableRef = ref);

    getId = (obj: any) => obj.id;
    getStudentId = (obj: any) => obj.ruuid;
    getRuuid = (obj: any) => obj.ruuid;

    getStrategicPlanOptionValue = (obj) => obj.id;
    getStrategicPlanOptionLabel = (obj) =>
        this.props.strategicPlanStore?.totalCountStrategicPlans !== undefined &&
        this.props.strategicPlanStore?.totalCountStrategicPlans > 1
            ? `${obj.name}`
            : obj.name;

    getStrategicPlansItemsOptions = () => {
        if (!this.state.strategicPlan) {
            return [];
        }
        const parent = this.state.strategicPlan;
        return this.props.strategicPlanStore!.individualItems.filter(
            (item) => item.strategic_plan_ruuid === parent.ruuid
        );
    };

    getStrategicPlanParentOptionValue = (obj) => obj.ruuid;

    getStrategicPlanParentOptionLabel = (obj) =>
        this.props.strategicPlanStore?.strategicPlanItems !== undefined &&
        this.props.strategicPlanStore?.strategicPlanItems.length > 1
            ? `${obj.name}`
            : obj.name;

    getStrategicPlanOptionStyles = (styles, { data }) => ({
        ...styles,
        fontWeight: !data.parent_id ? "bold" : "default",
        marginLeft:
            data.level === "second"
                ? "10px"
                : data.level === "third"
                    ? "20px"
                    : data.level === "fourth"
                        ? "30px"
                        : "default",
    });

    handleStrategicPlanItemChange = (strategicPlanItem) => {
        this.setState({ strategicPlanItems: strategicPlanItem });
    };

    handleStrategicPlanChange = (
        strategicPlan: StrategicPlanParent | undefined | null
    ) => {
        const currentPlanItems = this.props.currentTicket?.strategic_plan_items;
        if (
            currentPlanItems &&
            currentPlanItems.length > 0 &&
            strategicPlan?.ruuid === currentPlanItems[0].strategic_plan_ruuid
        ) {
            this.setState({
                strategicPlan,
                strategicPlanItems: this.toIndividualItems(currentPlanItems),
            });
            return;
        }
        this.setState({ strategicPlan, strategicPlanItems: [] });
    };

    handleReasonDueDate = (ev: React.ChangeEvent<HTMLInputElement>) => {
        this.setState({ reasonDueDateText: ev.currentTarget.value });
    };

    isValidReasonDueDateChange = () => {
        const { evaluationDate, oldDueDate } = this.state;

        const actionPlanUserAddDueDate =
            evaluationDate !== undefined && oldDueDate === undefined;
        const actionPlanUserRemoveDueDate =
            evaluationDate === undefined && oldDueDate !== undefined;

        if (evaluationDate && oldDueDate) {
            const newEvaluationDate =
                Utils.changeDatePickerTimeStampToEOD(evaluationDate);
            const oldEvaluationDate =
                Utils.changeDatePickerTimeStampToEOD(oldDueDate);
            if (newEvaluationDate !== oldEvaluationDate) {
                return true;
            }
        } else if (actionPlanUserRemoveDueDate || actionPlanUserAddDueDate) {
            return true;
        }
        return false;
    };

    shouldShowRequiredIndicator = (fieldName: string): boolean => {
        // Mirror the "isValid" logic: title is required for action plan create/edit.
        if (this.props.isPreview) return false;

        switch (fieldName) {
            case "title": {
                return true;
            }
            default:
                return false;
        }
    };

    renderInsightComponent = () => {
        return (
            <fieldset className="fieldset" style={{ height: "100%" }}>
                <legend className="fieldset">ActionPlan</legend>
                <div className="inner">
                    <Form.Group>
                        <Form.Label htmlFor="cqi-form-insight">
                            Title<span style={{ color: "red" }}>*</span>
                        </Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={2}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-insight"
                                name="cqi-form-insight"
                                onBlur={this.handleInsightBlur}
                                value={this.state.dashboardInterpretation || ""}
                                onChange={this.handleInsightChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_interpretation"
                                )}
                                placeholder="Provide a clear, specific title that summarizes the issue, opportunity, or focus of this plan."
                            />
                            {this.state.dashboardInterpretation && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ dashboardInterpretation: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                        <FieldValidationMessage
                            isValid={this.isInterpretationValid()}
                            hasInput={
                                this.state.insightHasDoneBlur
                                    ? !!this.state.dashboardInterpretation
                                    : false
                            }
                            message="Succinctly describe your observations about this ActionPlan"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Observation and Findings</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-progress"
                                value={this.state.progress}
                                onChange={this.handleProgressChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_progress"
                                )}
                                placeholder="Add insights and any actionable findings based on the data in the dashboard."
                            />
                            {this.state.progress && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ progress: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>
                            Proposed Action
                        </Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-ddd-title"
                                value={this.state.ddd}
                                onChange={this.handleDDDChange}
                                disabled={this.shouldDisableField("can_edit_ddd")}
                                placeholder="What specific actions must be taken? Consider including deadlines, task owners, and how progress will be measured."
                            />
                            {this.state.ddd && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ ddd: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Desired Outcome</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-ddd-objective"
                                value={this.state.objective}
                                onChange={this.handleObjectiveChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_objective"
                                )}
                                placeholder="What is the intended impact of these actions? What measurable improvement should result from these actions?"
                            />
                            {this.state.objective && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ objective: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Target for Closure</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-ddd-goal"
                                value={this.state.target}
                                onChange={this.handleGoalChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_target"
                                )}
                                placeholder="Identify the specific metric(s) that will determine you’ve met your goal(s)."
                            />
                            {this.state.target && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ target: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                </div>
            </fieldset>
        );
    };

    renderGeneralInsightComponent = () => {
        return (
            <fieldset className="fieldset">
                <legend className="fieldset">ActionPlan</legend>
                <div className="inner">
                    <Form.Group>
                        <Form.Label htmlFor="cqi-form-insight">
                            Title
                            {this.shouldShowRequiredIndicator("title") && (
                                <span style={{ color: "red" }}>*</span>
                            )}
                        </Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={2}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-insight"
                                name="cqi-form-insight"
                                onBlur={this.handleInsightBlur}
                                value={this.state.dashboardInterpretation}
                                onChange={this.handleInsightChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_interpretation"
                                )}
                                placeholder="Provide a clear, specific title that summarizes the issue, opportunity, or focus of this plan."
                            />
                            {this.state.dashboardInterpretation && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ dashboardInterpretation: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                        <FieldValidationMessage
                            isValid={this.isInterpretationValid()}
                            hasInput={
                                this.state.insightHasDoneBlur
                                    ? !!this.state.dashboardInterpretation
                                    : false
                            }
                            message="Succinctly describe your observations about this ActionPlan"
                        />
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Observation and Findings</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-progress"
                                value={this.state.progress}
                                onChange={this.handleProgressChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_progress"
                                )}
                                placeholder="Add insights and any actionable findings based on the data in the dashboard."
                            />
                            {this.state.progress && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ progress: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Proposed Action</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-ddd-title"
                                value={this.state.ddd}
                                onChange={this.handleDDDChange}
                                disabled={this.shouldDisableField("can_edit_ddd")}
                                placeholder="What specific actions must be taken? Consider including deadlines, task owners, and how progress will be measured."
                            />
                            {this.state.ddd && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ ddd: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Desired Outcome</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-ddd-objective"
                                value={this.state.objective}
                                onChange={this.handleObjectiveChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_objective"
                                )}
                                placeholder="What is the intended impact of these actions? What measurable improvement should result from these actions?"
                            />
                            {this.state.objective && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ objective: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                    <Form.Group>
                        <Form.Label>Target for Closure</Form.Label>
                        <div className="textarea-container">
                            <Form.Control
                                as="textarea"
                                rows={3}
                                style={{ resize: "vertical", overflowY: "auto" }}
                                id="cqi-form-ddd-goal"
                                value={this.state.target}
                                onChange={this.handleGoalChange}
                                disabled={this.shouldDisableField(
                                    "can_edit_target"
                                )}
                                placeholder="Identify the specific metric(s) that will determine you’ve met your goal(s)."
                            />
                            {this.state.target && (
                                <Button
                                    variant="link"
                                    className="clear-button"
                                    onClick={() => this.setState({ target: "" })}
                                >
                                    <CloseIcon width={24} height={24} />
                                </Button>
                            )}
                        </div>
                    </Form.Group>
                </div>
            </fieldset>
        );
    };

    getUserLabel = (u: User) => u.name;

    renderCollaborationComponent = () => {
        const { userStore, commonStore } = this.props;
        if (!userStore || !commonStore) return null;

        return (
            <fieldset className="fieldset">
                <legend className="fieldset">Collaboration</legend>
                <div className="inner">
                    <Row>
                        <Col>
                            <label className="form-label">{"Assignee"}<span style={{ color: "red" }}>*</span></label>
                            <CustomSelectWrapper
                                id="cqi-form-ddd-assignee-input"
                                classNamePrefix="collaboration-select"
                                isDisabled={this.shouldDisableField(
                                    "can_edit_assignee"
                                )}
                                onChange={this.handleAssigneeChange}
                                options={
                                    userStore && userStore.assignees
                                        ? toJS(userStore.assignees)
                                        : []
                                }
                                getOptionLabel={this.getUserLabel}
                                getOptionValue={this.getRuuid}
                                value={this.state.assignee}
                                placeholder="Select Assignee"
                                isClearable={true}
                            />
                            <FieldValidationMessage
                                isValid={this.state.assignee !== undefined}
                                hasInput={this.state.assignee !== undefined}
                                message="You must assign this ActionPlan to someone"
                            />
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div style={{ position: "relative" }}>
                                <label className="form-label">{"Collaborators"}</label>
                                <CustomSelectWrapper
                                    id="cqi-form-ddd-collaborator-input"
                                    classNamePrefix="collaboration-select"
                                    hideSelectedOptions={true}
                                    isDisabled={this.shouldDisableField(
                                        "can_edit_collaborators"
                                    )}
                                    isClearable={true}
                                    options={this.props.userStore!.assignees}
                                    value={this.state.collaborators}
                                    getOptionLabel={this.getUserLabel}
                                    getOptionValue={this.getRuuid}
                                    onChange={this.handleCollaboratorsChange}
                                    isMulti={true}
                                    placeholder="Select Collaborators"
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row>
                        <Col>
                            <div
                                id="cqi-form-classification-committee"
                                className="classification"
                            >
                                <label className="form-label">{"Committee"}</label>
                                <CustomSelectWrapper
                                    id="cqi-form-classification-committee-input"
                                    isDisabled={
                                        !commonStore.committees ||
                                        commonStore.committees.length === 0 ||
                                        this.shouldDisableField("can_edit_committees")
                                    }
                                    onChange={this.handleCommitteesChange}
                                    isClearable={true}
                                    isMulti={true}
                                    options={
                                        commonStore.committees
                                            ? commonStore.committees
                                            : []
                                    }
                                    placeholder="Select Committees"
                                    value={this.state.committees}
                                    getOptionLabel={this.getCommitteeLabel}
                                    getOptionValue={this.getId}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </fieldset>
        );
    };

    renderAttachmentComponent = () => {
        return (
            <fieldset className="fieldset margin-top">
                <legend className="fieldset">Attachments</legend>
                <Row className="inner">
                    <AttachmentSelector
                        attachments={this.state.attachments}
                        onChange={this.handleAttachmentChange}
                        dropRef={this.formRef}
                    />
                </Row>
            </fieldset>
        );
    };

    renderInfoBanner = () => {
        return (
            <div className="info-banner">
                <Info className="info-icon" size={16} />
                <span>
                    <strong>ActionPlan</strong> will capture a screenshot of
                    your dashboard. Ensure your dashboard is set up as desired
                    before proceeding.
                </span>
            </div>
        );
    };

    getCourseLabel = (c: Course) => c.name;
    getCommitteeLabel = (c: Committee) => c.name;

    renderClassificationComponent = () => {
        const { commonStore } = this.props;

        if (!commonStore) return null;

        return (
            <fieldset className="fieldset">
                <legend className="fieldset">Classification</legend>
                <div className="inner">
                    <div id="cqi-form-classification-course">
                        <label className="form-label">{"Course"}</label>
                        <CustomSelectWrapper
                            id="cqi-form-classification-course-input"
                            isDisabled={
                                !commonStore.courses ||
                                commonStore.courses.length === 0 ||
                                this.shouldDisableField("can_edit_course")
                            }
                            isClearable={true}
                            onChange={this.handleClassificationChange}
                            options={
                                commonStore.courses ? commonStore.courses : []
                            }
                            placeholder="Course"
                            value={this.state.course}
                            getOptionLabel={this.getCourseLabel}
                            getOptionValue={this.getId}
                        />
                    </div>
                    {this.props.labelStore && this.props.labelStore.labelGroups
                        ? this.renderLabelDropdowns()
                        : null}
                </div>
            </fieldset>
        );
    };

    renderStudentComponent = () => {
        const { commonStore } = this.props;
        if (!commonStore) return null;
        return (
            <fieldset className="fieldset">
                <legend className="fieldset">Student</legend>
                <div className="inner">
                    <Row>
                        <Col>
                            <CustomSelectWrapper
                                id="cqi-form-ddd-student-input"
                                classNamePrefix="students-select"
                                hideSelectedOptions={true}
                                isDisabled={
                                    this.props.isPreview ||
                                    this.shouldDisableField("can_edit_students")
                                }
                                isClearable={true}
                                options={toJS(this.props.commonStore!.students)}
                                value={this.state.students}
                                getOptionLabel={Utils.studentToStudentLabel}
                                getOptionValue={this.getStudentId}
                                onChange={this.handleStudentsChange}
                                isMulti={this.state.studentsIsMulti}
                            />
                            <div className="field-validation">
                                {this.props.commonStore!.students ===
                                undefined ||
                                this.props.commonStore!.students.length >
                                0 ? null : (
                                    <span className="text-muted">
                                        To select students, ensure you are
                                        assigned mentees and/or associated with
                                        at least one course
                                    </span>
                                )}
                            </div>
                        </Col>
                    </Row>
                </div>
            </fieldset>
        );
    };

    renderDueDate = () => {
        const userChosenSchoolInfo = Utils.getChosenSchoolInfo();
        return (
            <fieldset className="fieldset">
                <legend className="fieldset">Due Date</legend>
                <div className="inner">
                    {!userChosenSchoolInfo.is_admin ? null : (
                        <SimpleCheckbox
                            isChecked={
                                this.state.allowAssigneeToChangeDueDate &&
                                this.state.type === "task"
                            }
                            id="detail-form-assignee-due-date"
                            onChange={this.handleAllowAssigneeToChangeDueDate}
                            label="Allow assignee to change due date"
                            disabled={!this.state.evaluationDate}
                            className="checkbox-button"
                        />
                    )}
                    <DatePicker
                        placeholderText="Select or type a date (MM/DD/YYYY)..."
                        className="cqi-form-ddd-due-date form-control"
                        selected={
                            this.state.evaluationDate
                                ? new Date(this.state.evaluationDate)
                                : undefined
                        }
                        onChange={this.handleSelectDate}
                        onChangeRaw={(event) => this.handleManualDateInput(event.target.value)}
                        dateFormat="E MMM d yyyy"
                        showYearDropdown
                        showMonthDropdown
                        dropdownMode="select"
                        yearDropdownItemNumber={10}
                        scrollableYearDropdown
                        customInput={<DateInputWithIcon />}
                    />
                    {this.state.type === "task" && !this.props.currentTicket ? (
                        <FieldValidationMessage
                            isValid={this.state.evaluationDate !== undefined}
                            hasInput={!!this.state.evaluationDate}
                            message="This is not a valid date"
                        />
                    ) : null}
                    {this.renderReasonForTheChangeOfDueDate()}
                </div>
            </fieldset>
        );
    };

    renderReasonForTheChangeOfDueDate = () => {
        if (
            this.isValidReasonDueDateChange() &&
            this.props.currentTicket !== undefined
        ) {
            return (
                <Form.Group>
                    <Form.Label htmlFor="cqi-due-date-reason">
                        Due date change comments
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        id="cqi-due-date-reason"
                        name="cqi-due-date-reason"
                        value={this.state.reasonDueDateText}
                        onChange={this.handleReasonDueDate}
                    />
                    <FieldValidationMessage
                        isValid={
                            this.state.reasonDueDateText !== undefined ||
                            this.state.reasonDueDateText !== "" ||
                            this.props.currentTicket === undefined
                        }
                        hasInput={!!this.state.reasonDueDateText}
                        message="Enter a reason of the change of due dates."
                    />
                </Form.Group>
            );
        }
    };

    renderActionStatus = () => {
        let actionplanStatus = "N/A";
        if (this.props.currentTicket !== undefined) {
            if (this.props.currentTicket.status === "open") {
                actionplanStatus = "open";
            } else if (this.props.currentTicket.status === "closed") {
                const goalMetText = this.props.currentTicket!.target_met
                    ? "Goal met"
                    : "Goal not met";
                actionplanStatus = `closed (${goalMetText})`;
            }
        }

        if (this.props.currentTicket) {
            return (
                <fieldset className="fieldset">
                    <legend className="fieldset">ActionPlan Status</legend>
                    <div className="inner">
                        <div
                            className="ticket-status disabled"
                            style={{ marginRight: 10 }}
                        >
                            {actionplanStatus}
                        </div>
                        {this.renderEditStatus()}
                    </div>
                </fieldset>
            );
        } else {
            return null;
        }
    };

    renderEditStatus = () => {
        if (!this.props.currentTicket || this.props.isPreview) {
            return null;
        }

        if (
            (!this.props.isPreview && this.props.dashboard) ||
            this.props.currentTicket!.dashboard_id === null
        ) {
            if (
                this.props.ticketStore!.isFetchingTickets ||
                this.props.ticketStore!.isProcessing
            ) {
                return <Spinner animation="border" />;
            }
            const isActionplanClosed =
                this.props.currentTicket.status === "closed";
            return (
                <Button
                    onClick={this.toggleTaskStatus}
                    variant="secondary"
                    id="ticketStatusButton"
                    title={`${
                        isActionplanClosed ? "Re-Open" : "Close"
                    } ActionPlan`}
                    disabled={this.shouldDisableField("can_edit_status")}
                >
                    {`${
                        isActionplanClosed ? "Re-Open" : "Close"
                    } ActionPlan`}
                </Button>
            );
        }
        return (
            <Link
                to={{
                    pathname: Utils.getActionplanUrl(
                        this.props.currentTicket!.dashboard,
                        this.props.currentTicket!.ruuid
                    ),
                }}
            >
                <Button variant="secondary" onClick={this.handleActionPlanEdit}>
                    Go to ActionPlan
                </Button>
            </Link>
        );
    };

    getSnapshotLink = (viewType: string) => {
        const dashboardId =
            this.props.dashboard?.id || this.props.currentTicket?.dashboard_id;
        if (!dashboardId) {
            return;
        }
        const view = this.props.commonStore?.dashboardViews.find(
            (view) => view.view_type === viewType
        );
        if (!view) {
            return;
        }
        return (
            <>
                <Link
                    className="btn-no-border"
                    style={{ display: "inline-flex", alignItems: "flex-end" }}
                    onClick={() => {
                        const nextRefreshView = this.state.refreshView
                            ? Number(this.state.refreshView) + 1
                            : 0;
                        this.setState(
                            { refreshView: nextRefreshView.toString() },
                            () => window.scrollTo({ top: 0, behavior: "smooth" })
                        );
                    }}
                    to={Utils.getDashboardViewUrl(
                        dashboardId,
                        view.ruuid,
                        this.state.refreshView
                            ? Number(this.state.refreshView) + 1
                            : 0,
                        this.props.currentTicket?.ruuid
                    )}
                >
                    <label className="btn-no-border">Preview</label>
                </Link>
                <span className="mx-2">|</span>
            </>
        );
    };

    getSnapshotCopyButton = (viewType: string) => {
        const dashboardId =
            this.props.dashboard?.id || this.props.currentTicket?.dashboard_id;
        if (!dashboardId) {
            return;
        }
        const view = this.props.commonStore?.dashboardViews.find(
            (view) => view.view_type === viewType
        );
        if (!view) {
            return;
        }
        const nextRefreshView = this.state.refreshView
            ? Number(this.state.refreshView) + 1
            : 0;
        const copyUrl = `${window.location.origin}${Utils.getDashboardUrlNew(
            this.props.currentTicket!.dashboard,
            view.ruuid,
            nextRefreshView,
            this.props.currentTicket?.ruuid
        )}`;

        return (
            <CopyButton copyUrl={copyUrl} className="btn-no-border" size="sm">
                Copy Link
            </CopyButton>
        );
    };

    renderSnapshot = () => {
        if(!this.props.currentTicket){
            return null;
        }

        const dashboardId =
            this.props.dashboard?.id || this.props.currentTicket?.dashboard_id;
        if (!dashboardId) {
            return;
        }

        const orClosed = this.props.currentTicket.status === "closed" && !(this.props.currentTicket as Task).closed_dashboard_snapshot_url ? "or closed " : "";
        const noSnapshot = `To select an initial ${orClosed}snapshot, pick an image from the Attachments tab`;
        return (
            <fieldset className="fieldset">
                <legend className="fieldset">{this.props.currentTicket!.dashboard_id ? "Dashboard " : ""}Snapshot</legend>
                <div className="px-3 pb-2 pt-0">
                    {this.props.currentTicket && this.props.currentTicket.dashboard_thumb_url && this.props.currentTicket.dashboard_snapshot_url && this.props.currentTicket.dashboard_snapshot_url !== "" ?
                        <div className="d-flex align-items-end justify-content-between">
                            <TicketSnapshot
                                largeUrl={ this.props.currentTicket.dashboard_snapshot_url }
                                thumbUrl={ this.props.currentTicket.dashboard_thumb_url }/>
                            <div className="d-flex align-items-end">
                                {this.getSnapshotLink("open")}
                                {this.getSnapshotCopyButton("open")}
                            </div>
                        </div> : noSnapshot }
                    { this.props.currentTicket && this.props.currentTicket.closed_dashboard_snapshot_url ?
                        <div className="d-flex align-items-end justify-content-between">
                            <TicketSnapshot
                                largeUrl={ this.props.currentTicket.dashboard_snapshot_url }
                                thumbUrl={ this.props.currentTicket.dashboard_thumb_url }/>
                            <div className="d-flex align-items-end">
                                {this.getSnapshotLink("closed")}
                                {this.getSnapshotCopyButton("closed")}
                            </div>
                        </div> : null }
                </div>
            </fieldset>
        );
    };

    renderStrategicPlan = () => {
        return (
            <fieldset className="fieldset">
                <legend className="fieldset">
                    Institutional Effectiveness Plans
                </legend>
                <div className="inner ap-strategic-plan-select-box">
                    <Row>
                        <Col xs={4}>
                            <CustomSelectWrapper
                                name="ap-strategic-plan-select"
                                options={
                                    this.props.strategicPlanStore!
                                        .strategicPlanItems
                                }
                                getOptionValue={
                                    this.getStrategicPlanParentOptionValue
                                }
                                getOptionLabel={
                                    this.getStrategicPlanParentOptionLabel
                                }
                                onChange={this.handleStrategicPlanChange}
                                value={this.state.strategicPlan}
                                isMulti={false}
                                isClearable={true}
                                isDisabled={
                                    this.props.isPreview ||
                                    (this.props.currentTicket &&
                                        this.props.currentTicket.permissions &&
                                        !this.props.currentTicket.permissions
                                            .can_edit_strategic_plan)
                                }
                                placeholder="Select IEP"
                            />
                        </Col>
                        <Col xs={8}>
                            <CustomSelectWrapper
                                name="ap-strategic-plan-select"
                                options={this.getStrategicPlansItemsOptions()}
                                getOptionValue={
                                    this.getStrategicPlanOptionValue
                                }
                                getOptionLabel={
                                    this.getStrategicPlanOptionLabel
                                }
                                onChange={this.handleStrategicPlanItemChange}
                                value={this.state.strategicPlanItems}
                                isMulti={true}
                                isClearable={true}
                                isDisabled={
                                    this.props.isPreview ||
                                    (this.props.currentTicket &&
                                        this.props.currentTicket.permissions &&
                                        !this.props.currentTicket.permissions
                                            .can_edit_strategic_plan) ||
                                    !this.state.strategicPlan
                                }
                                styles={{
                                    option: this.getStrategicPlanOptionStyles,
                                }}
                                placeholder="Select IEP Item"
                            />
                        </Col>
                    </Row>
                </div>
            </fieldset>
        );
    };

    getTemplateOptionValue = (obj) => obj.id;
    getTemplateOptionLabel = (obj) => {
        return `${obj.display_id} | ${obj.name}`;
    };

    handleTemplateSelectionChange = (templateSelectionAP) => {
        if (!templateSelectionAP) {
            this.setState({
                templateSelection: undefined,
                dashboardInterpretation: "",
                target: "",
                progress: "",
                objective: "",
                ddd: "",
                course: undefined,
                committee: undefined,
                assignee: undefined,
                collaborators: undefined,
                strategicPlanItems: [],
                strategicPlan: null,
                evaluationDate: undefined,
                labels: {},
                students: [],
            });
        } else {
            const groups = toJS(this.props.labelStore!.labelGroups);
            let selectedLabels = toJS(templateSelectionAP.labels);
            selectedLabels = groups.reduce(
                (prev, cur: LabelGroup) =>
                    Object.assign({}, prev, {
                        [cur.id]: selectedLabels[cur.id]
                            ? Utils.humanSort(
                                selectedLabels[cur.id],
                                (label: Label) => label.name
                            )
                            : undefined,
                    }),
                {}
            );
            let student;
            if (templateSelectionAP.students[0]) {
                student = this.props.commonStore!.students
                    ? this.props.commonStore!.students.find(
                        (student) =>
                            student.ruuid ===
                            templateSelectionAP.students[0].ruuid
                    )
                    : undefined;
            }
            const strategicPlanItems = this.props.strategicPlanStore!
                .individualItems
                ? this.props.strategicPlanStore!.individualItems.filter(
                    (s: IndividualItem) =>
                        s.id === templateSelectionAP.strategic_plan_item_id
                )
                : [];
            const strategicPlan =
                strategicPlanItems.length > 0
                    ? this.props.strategicPlanStore!.strategicPlanItems.find(
                        (s: StrategicPlanParent) =>
                            s.ruuid ===
                            strategicPlanItems[0].strategic_plan_ruuid
                    )
                    : undefined;
            this.setState({
                templateSelection: templateSelectionAP
                    ? templateSelectionAP
                    : undefined,
                dashboardInterpretation:
                templateSelectionAP.dashboard_interpretation,
                target: templateSelectionAP.target,
                progress: templateSelectionAP.progress,
                objective: templateSelectionAP.objective,
                ddd: templateSelectionAP.ddd,
                course: this.props.commonStore!.courses
                    ? this.props.commonStore!.courses.find(
                        (c: Course) => c.id === templateSelectionAP.course_id
                    )
                    : undefined,
                committee: this.props.commonStore!.committees
                    ? this.props.commonStore!.committees.find(
                        (c: Committee) =>
                            c.id === templateSelectionAP.committee_id
                    )
                    : undefined,
                assignee: this.props.userStore!.assignees
                    ? this.props.userStore!.assignees.find(
                        (u: User) =>
                            u.ruuid === templateSelectionAP.assignee_ruuid
                    )
                    : undefined,
                collaborators: toJS(templateSelectionAP.collaborators).map(
                    (col: Collaborator) =>
                        Object.assign({}, col, { ruuid: col.faculty_ruuid })
                ),
                strategicPlanItems,
                strategicPlan: strategicPlan ? strategicPlan : null,
                evaluationDate:
                    templateSelectionAP.evaluation_date &&
                    templateSelectionAP.type === "task"
                        ? Utils.getValidDateFromString(
                            templateSelectionAP.evaluation_date! as string,
                            "YYYY-MM-DD HH:mm:ss"
                        )
                        : undefined,
                labels: selectedLabels,
                students: student,
                type: templateSelectionAP.type || "task",
            });
        }
    };

    renderTemplates = () => {
        return (
            <fieldset className="fieldset">
                <legend className="fieldset">Copy From</legend>
                <div className="inner">
                    <CustomSelectWrapper
                        options={this.props.ticketStore!.allTickets}
                        getOptionValue={this.getTemplateOptionValue}
                        getOptionLabel={this.getTemplateOptionLabel}
                        onChange={this.handleTemplateSelectionChange}
                        value={this.state.templateSelection}
                        isClearable={true}
                    />
                </div>
            </fieldset>
        );
    };

    enabledNotifications = (user_id: string) => {
        this.props.notificationViewerStore
            ?.enableNotificaciones(
                this.props.currentTicket?.ruuid as string,
                user_id
            )
            .then(() => {
                this.props.notificationViewerStore?.fetchUserViewersNotification(
                    this.props?.currentTicket?.ruuid as string
                );
            });
    };

    deactivateNotifications = (user_id: string) => {
        this.props.notificationViewerStore
            ?.deactivateNotificaciones(
                this.props.currentTicket?.ruuid as string,
                user_id
            )
            .then(() => {
                this.props.notificationViewerStore?.fetchUserViewersNotification(
                    this.props?.currentTicket?.ruuid as string
                );
            });
    };

    renderStatusDropdown = () => {
        if (!this.props.currentTicket) {
            return null;
        }

        if (
            this.props.ticketStore!.isFetchingTickets ||
            this.props.ticketStore!.isProcessing
        ) {
            return <Spinner animation="border" />;
        }

        const isActionplanClosed = this.props.currentTicket.status === "closed";
        const statusText = isActionplanClosed
            ? `Closed (${this.props.currentTicket.target_met ? "Goal met" : "Goal not met"})`
            : "Resolve ActionPlan";

        return (
            <Button
                onClick={this.toggleTaskStatus}
                variant={isActionplanClosed ? "success" : "warning"}
                id="ticketStatusButton"
                title={`${isActionplanClosed ? "Re-Open" : "Close"} ActionPlan`}
                disabled={this.shouldDisableField("can_edit_status")}
                size="sm"
            >
                {statusText}
            </Button>
        );
    };

    renderGoToDashboardButton = () => {
        if (!this.props.currentTicket || this.props.isPreview) {
            return null;
        }

        if (
            (!this.props.isPreview && this.props.dashboard) ||
            this.props.currentTicket.dashboard_id === null
        ) {
            return null;
        }

        return (
            <Link
                to={{
                    pathname: Utils.getActionplanUrl(
                        this.props.currentTicket.dashboard,
                        this.props.currentTicket.ruuid
                    ),
                }}
                className="pb-2"
            >
                <Button variant="primary" className="go-to-dashboard-button" onClick={this.handleActionPlanEdit}>
                    Go to Dashboard
                </Button>
            </Link>
        );
    };

    render() {
        const { ticketStore, commonStore, userStore, strategicPlanStore } = this.props;
        if (!ticketStore || !commonStore || !userStore || !strategicPlanStore) {
            return null;
        } else {
            return (
                <div
                    ref={this.props.formRef}
                    className={`observation-task-form  action-plan-form ${this.props.isPreview ? "preview" : ""}`}
                >
                    <Tab.Container defaultActiveKey={TAB_KEY_BY_NAME[this.props.initialTab ?? ""] ?? "1"}>
                        <Row className="align-items-center mb-3 row-custom-tabs">
                            <Col>
                                <Nav variant="tabs" className="ticket-tabs">
                                    <Nav.Item>
                                        <Nav.Link eventKey="1">ActionPlan Details</Nav.Link>
                                    </Nav.Item>
                                    {this.props.currentTicket && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="2">
                                                <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                                    Comments
                                                    <CommentBadge
                                                        count={
                                                            this.props.commentStore && this.props.commentStore.comments
                                                                ? this.props.commentStore.comments.length
                                                                : this.props.ticket
                                                                    ? this.props.ticket.comments.length
                                                                    : 0
                                                        }
                                                    />
                                                </span>
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {this.props.currentTicket && this.props.milestoneStore && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="3">
                                                <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                                    Milestones
                                                    <CommentBadge
                                                        count={this.getCompleteMilestones()}
                                                        secondCount={this.props.milestoneStore.milestones.length}
                                                    />
                                                </span>
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {this.props.currentTicket && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="4">
                                                <span style={{ display: "flex", alignItems: "center", gap: "0.25rem" }}>
                                                    Attachments
                                                    <CommentBadge
                                                        count={this.props.attachmentStore!.attachments.length}
                                                    />
                                                </span>
                                            </Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {this.props.currentTicket && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="5">History</Nav.Link>
                                        </Nav.Item>
                                    )}
                                    {this.props.currentTicket && (
                                        <Nav.Item>
                                            <Nav.Link eventKey="6">Viewers</Nav.Link>
                                        </Nav.Item>
                                    )}
                                </Nav>
                            </Col>
                            <Col xs="auto">
                                <div className="d-flex align-items-center gap-4">
                                    {this.renderGoToDashboardButton()}
                                    {this.renderStatusDropdown()}
                                </div>
                            </Col>
                        </Row>
                        <Tab.Content>
                            <Tab.Pane eventKey="1">
                                <div ref={this.handleRef}>
                                    <form>
                                        <div className="action-plan-form-content">
                                            <Col md={8}>
                                                <Row style={{ marginBottom: "10px" }}>
                                                    {!this.props.currentTicket ? (
                                                        <Col md={4}>{this.renderTemplates()}</Col>
                                                    ) : null}
                                                    <Col md={!this.props.currentTicket ? 8 : 12}>{this.renderStrategicPlan()}</Col>
                                                </Row>
                                                <Row>
                                                    <Col md={6}>
                                                        {this.props.dashboard ||
                                                        (this.props.currentTicket && this.props.currentTicket!.dashboard_id)
                                                            ? this.renderInsightComponent()
                                                            : this.renderGeneralInsightComponent()}
                                                    </Col>
                                                    <Col md={6}>
                                                        {this.renderClassificationComponent()}
                                                        {this.renderStudentComponent()}
                                                    </Col>
                                                </Row>
                                            </Col>
                                            <Col md={4} className="right-column">
                                                {this.renderDueDate()}
                                                {this.renderCollaborationComponent()}
                                                {this.renderSnapshot()}
                                            </Col>
                                        </div>
                                        <div className="d-flex justify-content-between align-items-center gap-3">
                                            {this.renderInfoBanner()}
                                            <div className="d-flex justify-content-between align-items-center gap-3">
                                                {this.props.currentTicket && this.props.currentTicket.updated && (
                                                    <div>
                                                        (<small className="text-muted">
                                                        Updated: {Utils.formatDate(new Date(this.props.currentTicket.updated), "MM/DD/YY h:mm A")}
                                                    </small>)
                                                    </div>
                                                )}
                                                {!this.props.isPreview && (
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            onClick={this.props.onCancel ? this.props.onCancel : undefined}
                                                            variant="secondary"
                                                            size="lg"
                                                            className="cancel-button"
                                                        >
                                                            Cancel
                                                        </Button>
                                                        <Button
                                                            id="save"
                                                            onClick={this.handleSave}
                                                            variant="primary"
                                                            disabled={
                                                                !this.isValid() ||
                                                                ticketStore.isProcessing ||
                                                                ticketStore.isFetchingTickets
                                                            }
                                                            size="lg"
                                                            className="save-button"
                                                        >
                                                            {ticketStore.isProcessing || ticketStore.isFetchingTickets || this.state.isSaving ? (
                                                                <Spinner animation="border" className="green-spinner" />
                                                            ) : (
                                                                "Save Changes"
                                                            )}
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </form>
                                </div>
                            </Tab.Pane>
                            {this.props.currentTicket && (
                                <Tab.Pane eventKey="2">
                                    {this.props.currentTicket &&
                                    this.props.currentTicket.ruuid &&
                                    this.props.currentTicket.permissions &&
                                    this.props.currentTicket.permissions.can_view_comment ? (
                                        <TicketCommentList
                                            ticketId={this.props.currentTicket.ruuid}
                                            dashboardId={this.props.dashboard?.id || this.props.currentTicket.dashboard_id}
                                            dashboardViews={this.props.commonStore?.dashboardViews}
                                            refreshView={this.props.refreshView}
                                            canAdd={this.props.currentTicket.permissions.can_add_comment}
                                            onDelete={this.handleDeleteComment}
                                            onAttachmentDelete={this.handleCommentAttachmentDelete}
                                            isOnDashboard={!!this.props.dashboard}
                                            isOnResource={this.props.dashboard?.dashboard_type === "BYODashboard"}
                                        />
                                    ) : (
                                        <Alert variant={"info"}>No Comments Available</Alert>
                                    )}
                                </Tab.Pane>
                            )}
                            {this.props.currentTicket && this.props.milestoneStore && (
                                <Tab.Pane eventKey="3">
                                    <ActionPlanMilestones actionPlan={this.props.currentTicket} />
                                </Tab.Pane>
                            )}
                            {this.props.currentTicket && (
                                <Tab.Pane eventKey="4">
                                    <div ref={this.assignAttachmentTableRef}>
                                        {this.props.currentTicket!.permissions.can_add_attachments ? (
                                            <Row className="align-items-end">
                                                <div>
                                                    {this.props.attachmentStore!.isUploading || this.props.attachmentStore!.isFetchingAttachments ? (
                                                        <Spinner animation="border" />
                                                    ) : (
                                                        <AttachmentSelector
                                                            attachments={[]}
                                                            onChange={this.handleDirectAttachmentChange}
                                                            dropRef={this.attachmentTableRef}
                                                        />
                                                    )}
                                                </div>
                                                <Col>
                                                    {this.state.showAttachmentAddedMessage ? (
                                                        <div className="attachment-added-message">
                                                            <i className="icon icon-check" />
                                                            Attachment Added
                                                        </div>
                                                    ) : null}
                                                </Col>
                                            </Row>
                                        ) : null}
                                        <AttachmentsTable
                                            actionplan={this.props.currentTicket}
                                            onDelete={this.handleDeleteAttachment}
                                            onSnapshotUpdate={this.handleSnapshotUpdate}
                                        />
                                    </div>
                                </Tab.Pane>
                            )}
                            {this.props.currentTicket && (
                                <Tab.Pane eventKey="5">
                                    <TicketHistory actionPlan={this.props.currentTicket} />
                                </Tab.Pane>
                            )}
                            {this.props.currentTicket && (
                                <Tab.Pane eventKey="6">
                                    {this.props.currentTicket &&
                                    this.props.currentTicket.ruuid &&
                                    this.props.notificationViewerStore ? (
                                        <TicketViewersList
                                            deactivateNotifications={this.deactivateNotifications}
                                            enableNotifications={this.enabledNotifications}
                                        />
                                    ) : (
                                        <Alert variant={"info"}>No Viewers Available</Alert>
                                    )}
                                </Tab.Pane>
                            )}
                        </Tab.Content>
                        {this.state.showSnapshotConfirm && this.props.currentTicket ? (
                            <SnapshotConfirmModal
                                show={this.state.showSnapshotConfirm}
                                onCancel={this.closeModal}
                                onWithSnapshot={this.handleCloseWithSnapshot}
                                onWithoutSnapshot={this.handleCloseWithoutSnapshot}
                            />
                        ) : null}
                        {this.state.showCloseConfirm && this.props.currentTicket ? (
                            <ActionplanCloseModal
                                isQuickActionplan={!this.props.currentTicket!.dashboard_id}
                                onCancel={this.closeModal}
                                onSave={this.handleCloseActionplan}
                                isSaving={this.state.isSaving}
                            />
                        ) : null}
                    </Tab.Container>
                </div>
            );
        }
    }
}
